/**
 * useAgentLiveRefresh — user-scoped SSE consumer for the Advocate.
 *
 * ADR-017 §3.2 + PR 2. Mirrors the SSE half of useDealLiveRefresh.js but
 * keyed by the authenticated user (deal events for ALL the user's deals
 * + memory + nudges + listings come through one connection). The endpoint
 * derives user_id from the auth claims, so this hook takes no arguments.
 *
 * Minimal PR 2 surface: connection management + a rolling event buffer.
 * PR 4 (Advocate Rail) will layer:
 *   - Supabase realtime fallback for cross-worker events
 *   - per-category invalidation timestamps for refetch coordination
 *   - reducer + agentStatus field on AgentContext
 *
 * Returns:
 *   events       — rolling array of recent events (newest first), capped
 *   isConnected  — boolean, true when the SSE stream is open
 *   lastEventAt  — timestamp of the most recent event (0 if none yet)
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../components/auth/AuthWrapper';
import { waitForStreamRetry } from '../utils/waitForStreamRetry';

const API_URL = import.meta.env.REACT_APP_API_URL || '';
const MAX_EVENTS = 50;

/**
 * Consume an SSE stream as an async iterator using fetch's ReadableStream
 * (EventSource doesn't support custom auth headers in-browser).
 *
 * Same shape as the helper in useDealLiveRefresh — duplicated for now so
 * PR 2 doesn't need to refactor that hook. PR 4 can extract to a shared
 * utility (`utils/sse.js`) if needed.
 */
async function* iterateSSE(response, abortSignal) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const onAbort = () => {
    try { reader.cancel(); } catch { /* already cancelled */ }
  };
  abortSignal.addEventListener('abort', onAbort, { once: true });

  try {
    while (true) {
      if (abortSignal.aborted) break;
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sepIdx;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        if (!frame || frame.startsWith(':')) continue;  // heartbeat / comment

        let eventType = 'message';
        const dataLines = [];
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) eventType = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
        }
        let data = {};
        if (dataLines.length) {
          try { data = JSON.parse(dataLines.join('\n')); }
          catch (err) {
            // eslint-disable-next-line no-console
            console.debug('[useAgentLiveRefresh] dropped malformed SSE frame:',
              dataLines.join('\n').slice(0, 200));
          }
        }
        yield { eventType, data };
      }
    }
  } finally {
    abortSignal.removeEventListener('abort', onAbort);
  }
}

export function useAgentLiveRefresh() {
  const [events, setEvents] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [lastEventAt, setLastEventAt] = useState(0);
  const { getAuthToken } = useAuth();

  const pushEvent = useCallback((evt) => {
    const ts = Date.now();
    setLastEventAt(ts);
    setEvents(prev => [{ ...evt, ts }, ...prev].slice(0, MAX_EVENTS));
  }, []);

  // Keep getAuthToken in a ref so the effect doesn't tear-down/rebuild the
  // SSE connection every time the auth provider hands us a new function
  // identity (Clerk's getToken can change on token refresh, ~every minute).
  // The internal while-loop already reconnects on real drops; we don't want
  // every parent re-render to also force a reconnect.
  const getAuthTokenRef = useRef(getAuthToken);
  useEffect(() => { getAuthTokenRef.current = getAuthToken; }, [getAuthToken]);

  useEffect(() => {
    const controller = new AbortController();
    let stopped = false;

    async function connect() {
      // Exponential backoff: 3s → 30s. Reset after an event or a stable
      // connection, not an empty 200 response that immediately closes.
      let backoffMs = 3000;
      const MAX_BACKOFF_MS = 30000;

      while (!stopped && !controller.signal.aborted) {
        try {
          const getToken = getAuthTokenRef.current;
          const token = getToken ? await getToken() : null;
          const resp = await fetch(`${API_URL}/api/v1/agent/stream`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            signal: controller.signal,
          });

          if (!resp.ok || !resp.body) {
            // 401 = token not minted yet (Clerk loading), 4xx/5xx transient.
            // eslint-disable-next-line no-console
            console.debug(
              `[useAgentLiveRefresh] SSE request ${resp.status}, retrying in ${backoffMs}ms`
            );
            setIsConnected(false);
            await waitForStreamRetry(backoffMs, controller.signal);
            backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
            continue;
          }

          const connectedAt = Date.now();
          setIsConnected(true);

          for await (const { eventType, data } of iterateSSE(resp, controller.signal)) {
            if (stopped) break;
            backoffMs = 3000;
            pushEvent({ eventType, data });
          }

          // Clean EOF can come from a proxy/server returning a short 200.
          // It needs the same delay as an error; otherwise this loops at
          // network speed and competes with the page's data requests.
          setIsConnected(false);
          if (Date.now() - connectedAt >= MAX_BACKOFF_MS) backoffMs = 3000;
          await waitForStreamRetry(backoffMs, controller.signal);
          backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
        } catch (err) {
          if (controller.signal.aborted) return;
          // eslint-disable-next-line no-console
          console.debug(
            `[useAgentLiveRefresh] SSE drop, reconnecting in ${backoffMs}ms:`,
            err?.message,
          );
          setIsConnected(false);
          await waitForStreamRetry(backoffMs, controller.signal);
          backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
        }
      }
    }

    connect();

    return () => {
      stopped = true;
      controller.abort();
      setIsConnected(false);
    };
    // Intentionally exclude getAuthToken — read via ref to avoid reconnect
    // churn on token-refresh identity changes. pushEvent has [] deps so it's
    // stable across renders. Effect runs once per mount (twice in StrictMode).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushEvent]);

  return { events, isConnected, lastEventAt };
}
